import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Tag,
  Modal,
  Form,
  message,
  Tooltip,
  Typography,
  Row,
  Col,
  Statistic,
  Drawer,
  Divider,
  Radio,
} from 'antd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sanitizeTrustedHtml } from '../../utils/sanitize';
import {
  useGetAdminStatsQuery,
  useGetAllMessagesQuery,
  useGetMessageTypesQuery,
  useSendMessageMutation,
  useScheduleMessageMutation,
  useDeleteMessageMutation,
  useGetAdminOrganizationsQuery,
  useGetAdminUsersQuery,
  type IMessage,
} from '../../services/nestjs-base-api';
import {
  PlusOutlined,
  SearchOutlined,
  MailOutlined,
  SendOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
  ApartmentOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuth } from '../../hooks/useAuth';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// ReactQuill configuration for Gmail-like editor
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link'],
    [{ 'align': [] }],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
  'link', 'align'
];

// Custom ReactQuill component for Ant Design Form integration.
//
// react-quill flattens multi-paragraph HTML into a single block when the value
// is present at MOUNT (a Quill 1.3.7 clipboard quirk on a freshly-created
// editor) but applies the same value correctly once mounted. So we render it
// UNCONTROLLED (empty at mount) and apply the incoming value imperatively, and
// we also rescue paragraph structure from "flat" paste sources. Same fix as
// the CRM rich-text editor (src/admin/components/crm/CrmRichTextEditor.tsx).
const QuillEditor: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const quillRef = useRef<ReactQuill>(null);
  const lastKnownRef = useRef<string>('');

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // Strip highlight (background colour) from pasted content — highlighted
    // merge fields in source docs otherwise carry their shading into the send.
    editor.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
      const ops = delta.ops as
        | Array<{ attributes?: Record<string, unknown> }>
        | undefined;
      ops?.forEach((op) => {
        if (op.attributes && 'background' in op.attributes) {
          delete op.attributes.background;
        }
      });
      return delta;
    });

    // Rescue paragraph structure on paste. Some sources express line breaks as
    // whitespace between inline elements rather than block/<br> tags; HTML
    // collapses that, so Quill's converter flattens the message into one line
    // (even while keeping inline bold/underline). We take over the paste,
    // convert the HTML, and compare its line count to the plain-text version —
    // trusting the HTML when it kept the structure and rebuilding from plain
    // text when it flattened.
    const root = editor.root;
    const countNewlines = (delta: { ops?: Array<{ insert?: unknown }> }) =>
      (delta.ops || []).reduce(
        (n, op) =>
          n +
          (typeof op.insert === 'string'
            ? (op.insert.match(/\n/g) || []).length
            : 0),
        0,
      );
    const onPaste = (e: ClipboardEvent) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const plain = (cd.getData('text/plain') || '').replace(/\r\n/g, '\n');
      if (!/\n/.test(plain)) return; // single-line: let Quill handle it
      const html = cd.getData('text/html');

      e.preventDefault();
      e.stopPropagation();
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      if (range && range.length) {
        editor.deleteText(range.index, range.length, 'user');
      }

      const convert = editor.clipboard.convert.bind(editor.clipboard) as unknown as (
        h: string,
      ) => { ops?: Array<{ insert?: unknown }> };
      const htmlNewlines = html ? countNewlines(convert(html)) : 0;
      const plainLines = plain.split('\n').filter((l) => l.trim()).length;
      if (html && htmlNewlines >= plainLines - 1) {
        editor.clipboard.dangerouslyPasteHTML(index, html, 'user');
      } else {
        const esc = (s: string) =>
          s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const htmlOut = plain
          .split(/\n{2,}/)
          .map((para) => `<p>${esc(para).replace(/\n/g, '<br>')}</p>`)
          .join('');
        editor.clipboard.dangerouslyPasteHTML(index, htmlOut, 'user');
      }
    };
    root.addEventListener('paste', onPaste, true);
    return () => root.removeEventListener('paste', onPaste, true);
  }, []);

  // Apply external value changes imperatively AFTER mount (the path that works),
  // silently so the load doesn't fire onChange and clobber the form value.
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const incoming = value || '';
    if (incoming === lastKnownRef.current) return; // our own echo
    if (incoming === editor.root.innerHTML) {
      lastKnownRef.current = incoming;
      return;
    }
    lastKnownRef.current = incoming;
    if (!incoming) {
      editor.setContents([] as never, 'silent' as never);
      return;
    }
    const convert = editor.clipboard.convert.bind(editor.clipboard) as unknown as (
      h: string,
    ) => never;
    editor.setContents(convert(incoming), 'silent' as never);
  }, [value]);

  const handleChange = (html: string) => {
    lastKnownRef.current = html;
    onChange?.(html);
  };

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      defaultValue=""
      onChange={handleChange}
      modules={quillModules}
      formats={quillFormats}
      placeholder={placeholder}
      style={{ height: '200px', marginBottom: '50px' }}
    />
  );
};

const AdminMessagesPage: React.FC = () => {
  const { user } = useAuth();

  // State variables
  const [searchText, setSearchText] = useState('');
  const [selectedMessageType, setSelectedMessageType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // RTK Query hooks
  const { data: statsData, isLoading: statsLoading } = useGetAdminStatsQuery();
  const { data: messageTypesData, isLoading: messageTypesLoading } = useGetMessageTypesQuery();
  const { data: messagesData, isLoading: messagesLoading, refetch: refetchMessages } = useGetAllMessagesQuery({
    search: searchText || undefined,
    messageTypeCode: selectedMessageType || undefined,
    status: selectedStatus || undefined,
    startDate: dateRange?.[0]?.toISOString(),
    endDate: dateRange?.[1]?.toISOString(),
    sortBy,
    sortOrder,
  });

  const [sendMessageMutation] = useSendMessageMutation();
  const [scheduleMessageMutation] = useScheduleMessageMutation();
  const [deleteMessageMutation] = useDeleteMessageMutation();

  // Fetch organizations and users for dropdown options
  const { data: organizationsData, isLoading: organizationsLoading } = useGetAdminOrganizationsQuery({
    jwtToken: user?.jwtToken || '',
    limit: 1000, // Get all organizations
  });
  const { data: usersData, isLoading: usersLoading } = useGetAdminUsersQuery({
    jwtToken: user?.jwtToken || '',
    limit: 1000, // Get all users
  });

  // Derived state from RTK Query data
  const messages = messagesData?.messages || [];
  const messageTypes = messageTypesData || [];
  const organizations = [...(organizationsData?.data || [])].sort((a, b) => a.name.localeCompare(b.name));
  const users = usersData?.data || [];
  const stats = statsData;
  const loading = messagesLoading || statsLoading || messageTypesLoading || organizationsLoading || usersLoading;

  // Compose message modal state
  const [composeModalVisible, setComposeModalVisible] = useState(false);
  const [composeForm] = Form.useForm();
  const [sendingMessage, setSendingMessage] = useState(false);

  // Message detail drawer state
  const [messageDetailVisible, setMessageDetailVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<IMessage | null>(null);


  // Load initial data

  // RTK Query automatically refetches when dependencies change

  // Messages are already filtered on the server side

  const columns: ColumnsType<IMessage> = [
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      render: (text, record) => (
        <div>
          <Button
            type="link"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              setSelectedMessage(record);
              setMessageDetailVisible(true);
            }}
          >
            <Text strong>{text}</Text>
          </Button>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.recipientCount} recipients
          </Text>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: ['messageType', 'name'],
      key: 'messageType',
      render: (text, record) => (
        <Tag color={record.messageType.color}>{text}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        // A concierge-held trial email is stored as a draft, but it is not one
        // an admin left half-written — it is a real email deliberately not
        // delivered, so it reads as its own state.
        if (status === 'draft' && (record as any)?.metadata?.suppressed) {
          return (
            <Tooltip title="Held because the organisation is being onboarded personally. Never delivered; open it to see what would have gone out.">
              <Tag color="orange">Held (concierge)</Tag>
            </Tooltip>
          );
        }
        const statusConfig = {
          draft: { color: 'default', text: 'Draft' },
          scheduled: { color: 'orange', text: 'Scheduled' },
          sent: { color: 'green', text: 'Sent' },
          failed: { color: 'red', text: 'Failed' },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const priorityConfig = {
          low: { color: 'default', text: 'Low' },
          normal: { color: 'blue', text: 'Normal' },
          high: { color: 'orange', text: 'High' },
          urgent: { color: 'red', text: 'Urgent' },
        };
        const config = priorityConfig[priority as keyof typeof priorityConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Performance',
      key: 'performance',
      render: (_, record) => (
        <div>
          <Text style={{ fontSize: '12px' }}>
            Opens: {record.readCount}/{record.recipientCount} ({Math.round((record.readCount / record.recipientCount) * 100)}%)
          </Text>
          <br />
          <Text style={{ fontSize: '12px' }}>
            Emails: {record.emailsSent}
          </Text>
        </div>
      ),
    },
    {
      title: 'Date',
      key: 'date',
      render: (_, record) => (
        <div>
          {record.status === 'scheduled' && record.scheduledAt ? (
            <>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              <Text style={{ fontSize: '12px' }}>
                {dayjs(record.scheduledAt).format('MMM D, YYYY h:mm A')}
              </Text>
            </>
          ) : record.sentAt ? (
            <>
              <SendOutlined style={{ marginRight: 4 }} />
              <Text style={{ fontSize: '12px' }}>
                {dayjs(record.sentAt).format('MMM D, YYYY h:mm A')}
              </Text>
            </>
          ) : (
            <Text style={{ fontSize: '12px' }}>
              Created {dayjs(record.createdAt).format('MMM D, YYYY h:mm A')}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedMessage(record);
                setMessageDetailVisible(true);
              }}
            />
          </Tooltip>
          {record.status === 'draft' && (
            <Tooltip title="Edit">
              <Button size="small" icon={<EditOutlined />} />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                Modal.confirm({
                  title: 'Delete Message',
                  content: 'Are you sure you want to delete this message? This action cannot be undone and will remove the message from all recipients.',
                  okText: 'Delete',
                  okType: 'danger',
                  cancelText: 'Cancel',
                  onOk: () => handleDeleteMessage(record.hash),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleComposeMessage = () => {
    setComposeModalVisible(true);
    composeForm.resetFields();
  };

  const handleDeleteMessage = async (messageHash: string) => {
    try {
      await deleteMessageMutation({ hash: messageHash }).unwrap();
      message.success('Message deleted successfully');
      // The RTK Query will automatically refetch the messages list due to cache invalidation
    } catch (error) {
      console.error('Failed to delete message:', error);
      message.error('Failed to delete message');
    }
  };

  const handleSendMessage = async (values: any) => {
    setSendingMessage(true);
    try {
      // Convert scheduledAt to ISO string if it exists
      const processedValues = {
        ...values,
        sendEmail: true, // Always send emails, users control preferences individually
        scheduledAt: values.scheduledAt ? values.scheduledAt.toISOString() : undefined,
      };

      console.log('Original values:', values);
      console.log('Processed values:', processedValues);
      console.log('scheduledAt exists?', !!processedValues.scheduledAt);

      if (processedValues.scheduledAt) {
        await scheduleMessageMutation(processedValues).unwrap();
        message.success('Message scheduled successfully!');
      } else {
        await sendMessageMutation(processedValues).unwrap();
        message.success('Message sent successfully!');
      }

      setComposeModalVisible(false);
      composeForm.resetFields();
      refetchMessages(); // Reload data to show the new message
    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const recipientTypeOptions = [
    { value: 'all_users', label: 'All Users', icon: <TeamOutlined /> },
    { value: 'organization', label: 'Organization Users', icon: <ApartmentOutlined /> },
    { value: 'specific_users', label: 'Specific Users', icon: <UserOutlined /> },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          <MailOutlined style={{ marginRight: '8px' }} />
          Message Administration
        </Title>
        <Text type="secondary">
          Send and manage messages to users across the platform
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Messages"
              value={stats?.overview.totalMessages || 0}
              prefix={<MailOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Sent Messages"
              value={stats?.overview.sentMessages || 0}
              prefix={<SendOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Scheduled"
              value={stats?.overview.scheduledMessages || 0}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Recipients"
              value={stats?.overview.totalRecipients || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search messages..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Message Type"
              allowClear
              value={selectedMessageType || undefined}
              onChange={setSelectedMessageType}
            >
              {messageTypes.map(type => (
                <Option key={type.code} value={type.code}>
                  <Tag color={type.color} style={{ margin: 0 }}>{type.name}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Status"
              allowClear
              value={selectedStatus || undefined}
              onChange={setSelectedStatus}
            >
              <Option value="draft">Draft</Option>
              <Option value="scheduled">Scheduled</Option>
              <Option value="sent">Sent</Option>
              <Option value="failed">Failed</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleComposeMessage}
              >
                Compose
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => refetchMessages()}>
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Messages Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={messages}
          rowKey="id"
          loading={loading}
          pagination={{
            total: messages.length,
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} messages`,
          }}
        />
      </Card>

      {/* Compose Message Modal */}
      <Modal
        title={
          <div>
            <MailOutlined style={{ marginRight: '8px' }} />
            Compose Message
          </div>
        }
        open={composeModalVisible}
        onCancel={() => setComposeModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={composeForm}
          layout="vertical"
          onFinish={handleSendMessage}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="messageType"
                label="Message Type"
                rules={[{ required: true, message: 'Please select a message type' }]}
              >
                <Select placeholder="Select message type">
                  {messageTypes.map(type => (
                    <Option key={type.code} value={type.code}>
                      <Tag color={type.color} style={{ margin: 0 }}>{type.name}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select priority' }]}
                initialValue="normal"
              >
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="normal">Normal</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="recipientType"
            label="Recipients"
            rules={[{ required: true, message: 'Please select recipients' }]}
          >
            <Radio.Group>
              {recipientTypeOptions.map(option => (
                <Radio key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.recipientType !== currentValues.recipientType
            }
          >
            {({ getFieldValue }) => {
              const recipientType = getFieldValue('recipientType');

              if (recipientType === 'organization') {
                return (
                  <Form.Item
                    name="organizationId"
                    label="Select Organization"
                    rules={[{ required: true, message: 'Please select an organization' }]}
                  >
                    <Select placeholder="Select organization">
                      {organizations.map(org => (
                        <Option key={org.id} value={org.id}>
                          {org.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }

              if (recipientType === 'specific_users') {
                return (
                  <Form.Item
                    name="userIds"
                    label="Select Users"
                    rules={[{ required: true, message: 'Please select users' }]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select users"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children?.toString() || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {users.map(user => (
                        <Option key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }

              return null;
            }}
          </Form.Item>

          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter a subject' }]}
          >
            <Input placeholder="Enter message subject" />
          </Form.Item>

          <Form.Item
            name="bodyHtml"
            label="Message Content"
            rules={[{ required: true, message: 'Please enter message content' }]}
          >
            <QuillEditor placeholder="Compose your message with rich formatting..." />
          </Form.Item>


          <Form.Item name="scheduledAt" label="Schedule for later (optional)">
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="Select date and time"
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              disabledTime={(current) => {
                // If the selected date is today, disable past hours/minutes
                if (current && current.isSame(dayjs(), 'day')) {
                  const now = dayjs();
                  return {
                    disabledHours: () => {
                      const hours = [];
                      for (let i = 0; i < now.hour(); i++) {
                        hours.push(i);
                      }
                      return hours;
                    },
                    disabledMinutes: (selectedHour) => {
                      if (selectedHour === now.hour()) {
                        const minutes = [];
                        for (let i = 0; i <= now.minute(); i++) {
                          minutes.push(i);
                        }
                        return minutes;
                      }
                      return [];
                    },
                  };
                }
                return {};
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginTop: '24px', marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={sendingMessage}
                icon={<SendOutlined />}
              >
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </Button>
              <Button onClick={() => setComposeModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Message Detail Drawer */}
      <Drawer
        title={
          <div>
            <MailOutlined style={{ marginRight: '8px' }} />
            Message Details
          </div>
        }
        placement="right"
        size="large"
        open={messageDetailVisible}
        onClose={() => setMessageDetailVisible(false)}
        styles={{body: {width: '100%'} }}
      >
        {selectedMessage && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Title level={4}>{selectedMessage.subject}</Title>
              <Space direction="vertical" size="small">
                <div>
                  <Text strong>Type: </Text>
                  <Tag color={selectedMessage.messageType.color}>
                    {selectedMessage.messageType.name}
                  </Tag>
                </div>
                <div>
                  <Text strong>Status: </Text>
                  <Tag color={selectedMessage.status === 'sent' ? 'green' : 'orange'}>
                    {selectedMessage.status === 'draft' && (selectedMessage as any)?.metadata?.suppressed
                      ? 'held (concierge) — never delivered'
                      : selectedMessage.status}
                  </Tag>
                </div>
                <div>
                  <Text strong>Priority: </Text>
                  <Tag>{selectedMessage.priority}</Tag>
                </div>
                <div>
                  <Text strong>Recipients: </Text>
                  <Text>{selectedMessage.recipientCount}</Text>
                </div>
              </Space>
            </div>

            <Divider />

            <div style={{ marginBottom: '16px' }}>
              <Title level={5}>Performance</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Opens"
                    value={selectedMessage.readCount}
                    suffix={`/ ${selectedMessage.recipientCount}`}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Open Rate"
                    value={Math.round((selectedMessage.readCount / selectedMessage.recipientCount) * 100)}
                    suffix="%"
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Emails Sent"
                    value={selectedMessage.emailsSent}
                  />
                </Col>
              </Row>
            </div>

            <Divider />

            <div>
              <Title level={5}>Message Content</Title>
              <div
                style={{
                  padding: '16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: '#fafafa',
                  textAlign: 'left',
                }}
                dangerouslySetInnerHTML={{ __html: sanitizeTrustedHtml(selectedMessage.bodyHtml) }}
              />
            </div>
          </div>
        )}
      </Drawer>

      <style>{`

        /* Custom styling for ReactQuill to match Gmail look */
        .ql-toolbar {
          border-top: 1px solid #d9d9d9 !important;
          border-left: 1px solid #d9d9d9 !important;
          border-right: 1px solid #d9d9d9 !important;
          border-bottom: none !important;
          border-radius: 6px 6px 0 0 !important;
          background: #fafafa !important;
        }

        .ql-container {
          border-bottom: 1px solid #d9d9d9 !important;
          border-left: 1px solid #d9d9d9 !important;
          border-right: 1px solid #d9d9d9 !important;
          border-top: none !important;
          border-radius: 0 0 6px 6px !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial !important;
        }

        .ql-editor {
          min-height: 200px;
          font-size: 14px;
          line-height: 1.6;
        }

        .ql-editor.ql-blank::before {
          color: #bfbfbf;
          font-style: normal;
        }

        .ql-toolbar .ql-formats {
          margin-right: 15px;
        }

        .ql-toolbar button:hover {
          color: #F59E0B !important;
        }

        .ql-toolbar button.ql-active {
          color: #F59E0B !important;
        }
      `}</style>
    </div>
  );
};

export default AdminMessagesPage;